<cs-header>
  <cs-header-title>{{ LL.programSettings.title() }}</cs-header-title>
  <cs-header-content>
    <cs-tab [(selection)]="selectedTab" urlParam="tab">
      <cs-tab-button [value]="ProgramSettingsTab.Training">
        <cs-icon name="barbell"></cs-icon>
        <cs-label>{{ LL.programSettings.tabs.training() }}</cs-label>
      </cs-tab-button>
      <cs-tab-button [value]="ProgramSettingsTab.Nutrition">
        <cs-icon name="dining"></cs-icon>
        <cs-label>{{ LL.programSettings.tabs.nutrition() }}</cs-label>
      </cs-tab-button>
    </cs-tab>
  </cs-header-content>
</cs-header>

<cs-tab-content-container [animate]="animateTabContent" [selection]="selectedTab"
  [swipeEnabled]="hasClientSmallScreen()" (swipeForward)="handleSwipeForward()" (swipeBack)="handleSwipeBack()">
  <!-- Training Tab Content -->
  <cs-tab-content [value]="ProgramSettingsTab.Training">
    <cs-content>
      <cs-card-list>

        <!-- Settings Card -->
        <cs-card [foldable]="false">
          <cs-card-title>{{ LL.programSettings.settings.title() }}</cs-card-title>
          <cs-card-content>

            <!-- Training Days Section -->
            <div class="subsection">
              <cs-item-list-header>
                <cs-label>{{ LL.programSettings.schedule.days() }}</cs-label>
              </cs-item-list-header>

              <div class="subsection-content">
                <div class="info-row training-days-row">
                  <div class="training-days">
                    <div class="training-days-grid">
                      @for (workout of trainingDays; track workout.weekday) {
                        <div class="training-day-item">
                          <cs-button tertiary (click)="handleRescheduleWorkout(workout, $event)">{{ workout.label }}, {{
                          workout.time }}</cs-button>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Advanced Training Methods Section -->
            <div class="subsection">
              <cs-item-list-header>
                <cs-label>{{ LL.programSettings.advancedTraining.title() }}</cs-label>
                <cs-button tertiary icon="edit" (click)="handleUpdateProgram()">
                  {{ LL.programSettings.advancedTraining.edit() }}
                </cs-button>
              </cs-item-list-header>

              <div class="subsection-content">
                <cs-radio-display [label]="LL.intake.advanced.myoReps()" [value]="getMyoRepsValue()">
                  <cs-tooltip iconType="help">
                    {{ getMyoRepsDescription() }}
                    @if (isMyoRepsEnabled()) {
                      <help-link helpId="@help:intake.advanced-training-techniques-myo-reps">
                        {{ LL.common.buttons.learnMore() }}
                      </help-link>
                    }
                  </cs-tooltip>
                </cs-radio-display>

                <cs-radio-display [label]="LL.intake.advanced.rmTesting()" [value]="getRMTestingValue()">
                  <cs-tooltip iconType="help">
                    {{ getRMTestingDescription() }}
                    @if (isRMTestingEnabled()) {
                      <help-link helpId="@help:intake.advanced-training-techniques-rm-testing">
                        {{ LL.common.buttons.learnMore() }}
                      </help-link>
                    }
                  </cs-tooltip>
                </cs-radio-display>
              </div>
            </div>

            <!-- Rest Timer Section -->
            <div class="subsection">
              <cs-item-list-header>
                <cs-label>{{ LL.workouts.restTimer.title() }}</cs-label>
                <cs-tooltip iconType="info">
                  {{ LL.workouts.restTimer.infoNative() }}
                </cs-tooltip>
              </cs-item-list-header>

              <!-- the default duration applies whichever way the timer is started -->
              <div class="subsection-content">
                <!-- the switch chooses whether the timer starts by itself after a set (#6014).
                     [ariaLabel] names it: the visible label is a sibling element, not a
                     <label> the toggle could take its name from -->
                <div class="info-row rest-timer-auto-start">
                  <cs-label class="info-label">{{ LL.workouts.restTimer.autoStartLabel() }}</cs-label>
                  <cs-toggle
                    [ariaLabel]="LL.workouts.restTimer.autoStartLabel()"
                    [checked]="isRestTimerAutoStart"
                    (checkedChange)="handleRestTimerAutoStartChange($event)">
                  </cs-toggle>
                </div>
                <div class="info-row">
                  <cs-label class="info-label">{{ LL.workouts.restTimer.defaultLabel() }}</cs-label>
                  <div class="rest-timer-stepper">
                    <cs-button tertiary icon="remove" [disabled]="!canDecRestDefault()"
                      (click)="handleRestDefaultStep(-1)"></cs-button>
                    @if (isTouchDevice()) {
                      <button type="button" class="rest-timer-roller-trigger"
                        (click)="handleRestDefaultRollerOpen($event)">
                        {{ restDefaultLabel }}
                      </button>
                    } @else {
                      <!-- [ariaLabel] names the picker: the stepper buttons share this row,
                           so there is no row text the select can take its name from -->
                      <cs-select class="rest-timer-select"
                        [ariaLabel]="LL.workouts.restTimer.defaultLabel()"
                        [value]="restDefaultSeconds"
                        (valueChange)="handleRestDefaultSelect($event)">
                        <!-- opt.label is a formatted M:SS duration, not translatable prose -->
                        @for (opt of restTimerOptions; track opt.seconds) {
                          <cs-select-option [value]="opt.seconds" [label]="opt.label | untranslated"></cs-select-option>
                        }
                      </cs-select>
                    }
                    <cs-button tertiary icon="add" [disabled]="!canIncRestDefault()"
                      (click)="handleRestDefaultStep(1)"></cs-button>
                  </div>
                </div>
              </div>
            </div>

            <!-- More Settings Foldable Section -->
            <div class="advanced-section">
              <cs-button tertiary icon-right icon="{{isAdvancedExpanded ? 'arrow-dropup' : 'arrow-dropdown'}}"
                (click)="toggleAdvanced()">
                {{ LL.programSettings.moreSettings.toggle() }}
              </cs-button>

              @if (isAdvancedExpanded) {
                <div class="advanced-content">
                  <cs-text-alert type="info">
                    {{ LL.programSettings.moreSettings.info() }}
                  </cs-text-alert>
                  <!-- Schedule Subsection -->
                  <div class="subsection">
                    <cs-item-list-header>
                      <cs-label>{{ LL.programSettings.schedule.title() }}</cs-label>
                      <cs-button secondary small icon="settings" (click)="handleUpdateSchedule()">
                        {{ hasClientSmallScreen() ? LL.programSettings.buttons.update() : LL.programSettings.buttons.updateProgram() }}
                      </cs-button>
                    </cs-item-list-header>
                    <div class="subsection-content">
                      <div class="info-row">
                        <cs-label class="info-label">{{ LL.programSettings.schedule.frequency() }}</cs-label>
                        <cs-label class="info-value">{{ getWorkoutsPerWeek() }} {{ LL.programSettings.schedule.daysPerWeek() }}</cs-label>
                      </div>
                      <div class="info-row">
                        <cs-label class="info-label">{{ LL.programSettings.schedule.sleep() }}</cs-label>
                        <cs-label class="info-value">{{ formatSleepHours() }} {{ LL.programSettings.schedule.hoursPerNight() }}</cs-label>
                      </div>
                    </div>
                  </div>
                  <!-- Lifestyle Subsection -->
                  <div class="subsection">
                    <cs-item-list-header>
                      <cs-label>{{ LL.programSettings.lifestyle.title() }}</cs-label>
                      <cs-button secondary small icon="settings" (click)="handleUpdateLifestyle()">
                        {{ hasClientSmallScreen() ? LL.programSettings.buttons.update() : LL.programSettings.buttons.updateProgram() }}
                      </cs-button>
                    </cs-item-list-header>
                    <div class="subsection-content">
                      <cs-radio-display [label]="LL.programSettings.lifestyle.activityLevel()"
                        [value]="getActivityLevelValue()" [description]="getActivityLevelDescription()">
                      </cs-radio-display>
                      <cs-radio-display [label]="LL.programSettings.lifestyle.stressLevel()"
                        [value]="getStressLevelValue()" [description]="getStressLevelDescription()">
                      </cs-radio-display>
                    </div>
                  </div>
                  <!-- Training Volume Subsection -->
                  <div class="subsection">
                    <cs-item-list-header>
                      <cs-label>{{ LL.programSettings.volume.title() }}</cs-label>
                    </cs-item-list-header>
                    <div class="subsection-content">
                      <div class="info-row with-button">
                        <cs-radio-display [label]="LL.programSettings.volume.priority()"
                          [value]="getPhysiquePriorityValue()" [customText]="getPhysiquePriorityCustomText()"
                          [description]="getPhysiquePriorityDescription()">
                        </cs-radio-display>
                        <cs-button secondary small icon="settings" (click)="handleUpdatePriority()">
                          {{ hasClientSmallScreen() ? LL.programSettings.buttons.update() : LL.programSettings.buttons.updateProgram() }}
                        </cs-button>
                      </div>
                      <div class="info-row with-button" id="setsPerWeekRow">
                        <cs-radio-display [label]="LL.programSettings.volume.setsPerWeek()"
                          [value]="getSetsPerWeekValue()" [customText]="getSetsPerWeekCustomText()"
                          [description]="getSetsPerWeekDescription()">
                          @if (isSetsPerWeekRecommended()) {
                            <cs-badge color="gray">{{ LL.common.ui.recommended() }}</cs-badge>
                          }
                          <!-- #6589: the workouts can still be built for a different number than
                               the saved target, after a "Keep My Current Workouts" save -->
                          @if (setsPerWeek.currentPlan !== undefined) {
                            <div radioDisplayNote id="setsPerWeekCurrentPlan">
                              <cs-note light>{{ LL.intake.training.currentPlanVolume({ volume: setsPerWeek.currentPlan }) }}</cs-note>
                              <cs-tooltip small iconType="help"
                                [text]="LL.programSettings.volume.setsPerWeekCurrentPlanTooltip({ volume: setsPerWeek.currentPlan })"></cs-tooltip>
                            </div>
                          }
                        </cs-radio-display>
                        <cs-button secondary small icon="settings" (click)="handleUpdateVolume()">
                          {{ hasClientSmallScreen() ? LL.programSettings.buttons.update() : LL.programSettings.buttons.updateProgram() }}
                        </cs-button>
                      </div>
                      <!-- Workout split (#4591), see design-docs/training-splits.md. Update opens
                           the same intake-form step the choice lives on, exactly like the two
                           rows above. -->
                      <div class="info-row with-button" id="trainingSplitRow">
                        <cs-radio-display [label]="LL.intake.trainingSplit.title()"
                          [value]="getTrainingSplitValue()" [description]="getTrainingSplitDescription()">
                        </cs-radio-display>
                        <cs-button secondary small icon="settings" (click)="handleUpdateTrainingSplit()">
                          {{ hasClientSmallScreen() ? LL.programSettings.buttons.update() : LL.programSettings.buttons.updateProgram() }}
                        </cs-button>
                      </div>
                      @if (muscleVolumeSummary) {
                        <div class="info-row muscle-volume-row">
                          <div class="muscle-volume-summary">
                            <cs-label class="info-label">{{ LL.training.trainingProgram.muscleVolume() }}</cs-label>
                            <cs-label class="info-value">{{ muscleVolumeSummary }}</cs-label>
                          </div>
                          <cs-button secondary small icon="magic-wand" (click)="handleViewMuscleVolume()">
                            {{ LL.programSettings.buttons.volumeDetails() }}
                          </cs-button>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          </cs-card-content>
        </cs-card>

      </cs-card-list>
    </cs-content>
  </cs-tab-content>

  <!-- Nutrition Tab Content -->
  <cs-tab-content [value]="ProgramSettingsTab.Nutrition">
    <cs-content>
      <cs-card-list>

        <!-- Nutrition Plan Card -->
        @if (nutrition) {
          <cs-nutrition-plan-card [nutrition]="nutrition" [trainingGoal]="trainingGoal" [editable]="true"
            (edit)="handleEditNutritionPlan()">
          </cs-nutrition-plan-card>
        }

        <!-- Settings Card -->
        <cs-card [foldable]="false">
          <cs-card-title>{{ LL.programSettings.settings.title() }}</cs-card-title>
          <cs-card-content>

            <!-- Meal Times Section -->
            <div class="subsection">
              <cs-item-list-header>
                <cs-label>{{ LL.programSettings.nutrition.mealTimes() }}</cs-label>
              </cs-item-list-header>

              <div class="subsection-content">
                <div class="info-row training-days-row">
                  <div class="training-days">
                    <div class="training-days-grid">
                      @for (meal of mealTimes; track meal.itemReference) {
                        <div class="training-day-item">
                          <span class="meal-label">{{ meal.label }}, </span>
                          <cs-button tertiary (click)="handleRescheduleMeal(meal, $event)">{{ meal.time }}</cs-button>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Nutrition Options Section -->
            <div class="subsection">
              <cs-item-list-header>
                <cs-label>{{ LL.intake.nutritionOptions.header() }}</cs-label>
                <cs-button tertiary icon="edit" (click)="handleUpdateNutritionOptions()">
                  {{ LL.programSettings.advancedTraining.edit() }}
                </cs-button>
              </cs-item-list-header>

              <div class="subsection-content">
                <cs-radio-display [label]="LL.intake.nutritionOptions.macroTargets()"
                  [value]="getMacroTargetsValue()" [description]="getMacroTargetsDescription()">
                  @if (isMacroTargetsRecommended()) {
                    <cs-badge color="gray">{{ LL.common.ui.recommended() }}</cs-badge>
                  }
                </cs-radio-display>

                <cs-radio-display [label]="LL.intake.nutritionOptions.macroTracking()"
                  [value]="getMacroTrackingValue()" [description]="getMacroTrackingDescription()">
                  @if (isMacroTrackingRecommended()) {
                    <cs-badge color="gray">{{ LL.common.ui.recommended() }}</cs-badge>
                  }
                </cs-radio-display>

                <cs-radio-display [label]="LL.intake.nutritionOptions.trackingType()"
                  [value]="getTrackingTypeValue()" [description]="getTrackingTypeDescription()">
                  @if (isTrackingTypeRecommended()) {
                    <cs-badge color="gray">{{ LL.common.ui.recommended() }}</cs-badge>
                  }
                </cs-radio-display>

                <cs-radio-display [label]="LL.intake.advanced.calorieCycling()"
                  [value]="getCalorieCyclingValue()">
                  <cs-tooltip iconType="help">
                    {{ getCalorieCyclingDescription() }}
                    @if (usesCalorieCycling(declaredState)) {
                      <help-link helpId="@help:intake.advanced-training-techniques-calorie-cycling"
                        >
                        {{ LL.common.buttons.learnMore() }}
                      </help-link>
                    }
                  </cs-tooltip>
                </cs-radio-display>
              </div>
            </div>

            <!-- More Settings Foldable Section -->
            <div class="advanced-section">
              <cs-button tertiary icon-right icon="{{isNutritionAdvancedExpanded ? 'arrow-dropup' : 'arrow-dropdown'}}"
                (click)="toggleNutritionAdvanced()">
                {{ LL.programSettings.moreSettings.toggle() }}
              </cs-button>

              @if (isNutritionAdvancedExpanded) {
                <div class="advanced-content">
                  <cs-text-alert type="info">
                    {{ LL.programSettings.moreSettings.info() }}
                  </cs-text-alert>
                  <!-- Goal Subsection -->
                  <div class="subsection">
                    <cs-item-list-header>
                      <cs-label>{{ LL.programSettings.nutrition.goal() }}</cs-label>
                      <cs-button secondary small icon="settings" (click)="handleUpdateGoal()">
                        {{ hasClientSmallScreen() ? LL.programSettings.buttons.update() : LL.programSettings.buttons.updateProgram() }}
                      </cs-button>
                    </cs-item-list-header>
                    <div class="subsection-content">
                      <cs-radio-display [label]="LL.programSettings.nutrition.primaryGoal()" [value]="goalText"
                        [description]="getGoalDescription()">
                      </cs-radio-display>
                    </div>
                  </div>
                  <!-- Schedule Subsection -->
                  <div class="subsection">
                    <cs-item-list-header>
                      <cs-label>{{ LL.programSettings.nutrition.schedule() }}</cs-label>
                      <cs-button secondary small icon="settings" (click)="handleUpdateNutritionSchedule()">
                        {{ hasClientSmallScreen() ? LL.programSettings.buttons.update() : LL.programSettings.buttons.updateProgram() }}
                      </cs-button>
                    </cs-item-list-header>
                    <div class="subsection-content">
                      <div class="info-row">
                        <cs-label class="info-label">{{ LL.programSettings.nutrition.mealFrequency() }}</cs-label>
                        <cs-label class="info-value">{{ getMealFrequency() }}</cs-label>
                      </div>
                    </div>
                  </div>
                  <!-- Energy Balance Subsection -->
                  <div class="subsection">
                    <cs-item-list-header>
                      <cs-label>{{ LL.programSettings.nutrition.energyBalance() }}</cs-label>
                      <cs-button secondary small icon="settings" (click)="handleUpdateEnergyBalance()">
                        {{ hasClientSmallScreen() ? LL.programSettings.buttons.update() : LL.programSettings.buttons.updateProgram() }}
                      </cs-button>
                    </cs-item-list-header>
                    <div class="subsection-content">
                      <cs-radio-display [label]="getEnergyBalanceLabel()" [value]="getEnergyBalanceValue()"
                        [customText]="getEnergyBalanceCustomText()" [description]="getEnergyBalanceDescription()">
                        @if (isEnergyBalanceRecommended()) {
                          <cs-badge color="gray">{{ LL.common.ui.recommended() }}</cs-badge>
                        }
                      </cs-radio-display>
                    </div>
                  </div>
                </div>
              }
            </div>

          </cs-card-content>
        </cs-card>
      </cs-card-list>
    </cs-content>
  </cs-tab-content>
</cs-tab-content-container>